import { createContext, useContext, useEffect, useMemo } from "react"
import { NotFoundError } from "api/errors"
import { isUuid } from "helpers/types/uuid"
import { useGetQuery } from "hooks/queries"
import { useQueryClient } from "@tanstack/react-query"
import { getThreadsQueryKey } from "connected-components/ai-teammate/utils/threads-cache"
import { useAccountAlias, useAreEnabledFeaturesResolved, useIsScheduledWorkflowsEnabled } from "resources/account"
import { useScheduledWorkflow, useScheduledWorkflowDeliverable } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflow, ScheduledWorkflowDeliverable } from "resources/scheduled-workflows/types"
import type { ChatSurfaceView } from "./store/agent-chat-slice"
import { deliverableOf, isWorkflowDraftArtifact, sharedArtifactResourceId } from "./chatbox/artifact-contract"
import type { ArtifactEnvelope, FileArtifact } from "./chatbox/artifact-contract"
import { type ChatArtifactResponse, useChatArtifact, useSharedArtifacts } from "./chatbox/resource-sharing"
import { isSkillPackageArtifact } from "./chatbox/skill-package-artifact"
import { useKellyChatOptional } from "./chatbox/kelly-chat-context"

export type KellyLinkLoad<Value> = { status: "pending" } | { status: "not-found" } | { status: "error", retry: () => void } | { status: "found", value: Value }

function loadOfQuery<Value>({ data, error, refetch }: { data: Value | undefined, error: unknown, refetch: () => unknown }): KellyLinkLoad<Value> {
  if (data !== undefined) return { status: "found", value: data }
  if (error instanceof NotFoundError) return { status: "not-found" }
  return error == null ? { status: "pending" } : { status: "error", retry: () => void refetch() }
}

function useReportLoad(deliverableId: string | null): KellyLinkLoad<ScheduledWorkflowDeliverable> {
  const isEnabled = useIsScheduledWorkflowsEnabled()
  const isValid = deliverableId != null && isUuid(deliverableId)
  const query = useScheduledWorkflowDeliverable(isValid && isEnabled ? deliverableId : null)
  if (deliverableId != null && (!isValid || !isEnabled)) return { status: "not-found" }
  return loadOfQuery(query)
}

function useSharedArtifactLoad(resourceId: string | null): KellyLinkLoad<{ artifact: FileArtifact, name: string }> {
  const { artifacts, isLoading, isFetching, isError, refetch } = useSharedArtifacts(resourceId != null)
  const row = resourceId == null ? undefined : artifacts.find((artifact) => artifact.id === resourceId)
  return useMemo(() => {
    if (resourceId == null) return { status: "pending" }
    if (row != null) {
      return {
        status: "found",
        value: { name: row.name, artifact: { id: row.sourceArtifactId, type: "file", filename: row.filename, contentType: row.contentType, resourceId: row.id } },
      }
    }
    if (isLoading || isFetching) return { status: "pending" }
    return isError ? { status: "error", retry: () => void refetch() } : { status: "not-found" }
  }, [resourceId, row, isLoading, isFetching, isError, refetch])
}

function useWorkflowLoad(scheduledWorkflowId: string | null): KellyLinkLoad<ScheduledWorkflow> {
  const isEnabled = useIsScheduledWorkflowsEnabled()
  const isValid = scheduledWorkflowId != null && isUuid(scheduledWorkflowId)
  const query = useScheduledWorkflow(isValid && isEnabled ? scheduledWorkflowId : null)
  if (scheduledWorkflowId != null && (!isValid || !isEnabled)) return { status: "not-found" }
  return loadOfQuery(query)
}

function useThreadLoad(threadId: string | null): KellyLinkLoad<string> {
  const controller = useKellyChatOptional()
  const queryClient = useQueryClient()
  const accountAlias = useAccountAlias()
  const isListed = threadId != null && controller != null
    && (controller.adoptedThreadIds.has(threadId) || controller.threads.some((thread) => thread.threadId === threadId))
  const isUnlisted = threadId != null && controller != null && !isListed && controller.isThreadsLoaded && !controller.isThreadsErrored
  const threadRead = useGetQuery<unknown>({
    url: `/api/${accountAlias}/ai/chat/threads/${encodeURIComponent(threadId ?? "")}?limit=1`,
    key: ["chat-thread", accountAlias, threadId],
    enabled: isUnlisted,
  })

  if (threadId == null || controller == null) return { status: "pending" }
  if (isListed) return { status: "found", value: threadId }
  if (controller.isThreadsErrored) return { status: "error", retry: () => void queryClient.refetchQueries({ queryKey: getThreadsQueryKey(accountAlias), exact: true }) }
  if (!controller.isThreadsLoaded) return { status: "pending" }
  // A refetch that 404s keeps the last data, so a thread deleted since its first read must still read as not found
  const isDeleted = threadRead.error instanceof NotFoundError
  return loadOfQuery({ data: threadRead.data === undefined || isDeleted ? undefined : threadId, error: threadRead.error, refetch: threadRead.refetch })
}

function useChatArtifactLoad(artifactId: string | null): KellyLinkLoad<ChatArtifactResponse> {
  return loadOfQuery(useChatArtifact(artifactId))
}

export const KELLY_LINK_TYPES = {
  reports: { hasListView: false, surfaceView: "workflows", useLoad: useReportLoad },
  artifacts: { hasListView: true, surfaceView: "artifacts", useLoad: useSharedArtifactLoad },
  "scheduled-workflows": { hasListView: true, surfaceView: "workflows", useLoad: useWorkflowLoad },
  threads: { hasListView: false, surfaceView: "conversation", useLoad: useThreadLoad },
  "chat-artifacts": { hasListView: false, surfaceView: "conversation", useLoad: useChatArtifactLoad },
} satisfies Record<string, { hasListView: boolean, surfaceView: ChatSurfaceView | null, useLoad: (id: string | null) => KellyLinkLoad<unknown> }>

export type KellyLinkType = keyof typeof KELLY_LINK_TYPES
export type KellyLinkValue<Type extends KellyLinkType> = ReturnType<(typeof KELLY_LINK_TYPES)[Type]["useLoad"]> extends KellyLinkLoad<infer Value> ? Value : never

export interface KellyLink {
  type: KellyLinkType | null
  id: string | null
}

export function isKellyLinkType(segment: string): segment is KellyLinkType {
  return Object.keys(KELLY_LINK_TYPES).includes(segment)
}

export function parseKellyLink(pathname: string): KellyLink | null {
  const [, kelly, segment, id, ...rest] = pathname.split("/").filter(Boolean)
  if (kelly !== "kelly-ai" || rest.length > 0) return null
  if (segment == null) return { type: null, id: null }
  if (!isKellyLinkType(segment)) return null
  if (id == null) return KELLY_LINK_TYPES[segment].hasListView ? { type: segment, id: null } : null
  try {
    return { type: segment, id: decodeURIComponent(id) }
  } catch {
    return null
  }
}

export function kellyLinkPath(accountAlias: string, type: KellyLinkType | null, id: string | null): string {
  const base = `/${encodeURIComponent(accountAlias)}/kelly-ai`
  if (type == null) return base
  if (id == null) return KELLY_LINK_TYPES[type].hasListView ? `${base}/${type}` : base
  return `${base}/${type}/${encodeURIComponent(id)}`
}

export function kellyNotFoundPath(accountAlias: string): string {
  return `/${encodeURIComponent(accountAlias)}/404`
}

export function historyIndex(state: unknown, key: "idx" | "kellyStart"): number | null {
  const value = typeof state === "object" && state !== null ? (state as Record<string, unknown>)[key] : undefined
  return typeof value === "number" ? value : null
}

export function kellyLinkOfView(surfaceView: ChatSurfaceView, threadId: string | null): KellyLink {
  const listType = Object.keys(KELLY_LINK_TYPES).filter(isKellyLinkType).find((type) => KELLY_LINK_TYPES[type].hasListView && KELLY_LINK_TYPES[type].surfaceView === surfaceView)
  if (listType != null) return { type: listType, id: null }
  return threadId == null ? { type: null, id: null } : { type: "threads", id: threadId }
}

// `idx` is react-router's own position counter and `usr` holds the state Kelly passes; both are internal shapes.
export function kellyStartIndex(): number | null {
  const state: unknown = window.history.state
  const userState: unknown = typeof state === "object" && state !== null ? (state as { usr?: unknown }).usr : undefined
  return historyIndex(userState, "kellyStart") ?? historyIndex(state, "idx")
}

export function kellyMinimiseSteps(): number | null {
  const start = kellyStartIndex()
  const index = historyIndex(window.history.state, "idx")
  if (start == null || index == null || start <= 0 || start > index) return null
  return start - 1 - index
}

export function kellyLinkOf(artifact: ArtifactEnvelope | null): KellyLink | null {
  if (artifact == null) return null
  const resourceId = sharedArtifactResourceId(artifact)
  if (resourceId != null) return { type: "artifacts", id: resourceId }
  const deliverable = deliverableOf(artifact)
  if (deliverable != null) return { type: "reports", id: deliverable.deliverableId }
  if (!isSkillPackageArtifact(artifact) && !isWorkflowDraftArtifact(artifact)) return { type: "chat-artifacts", id: artifact.id }
  return null
}

export interface KellyLinkContextValue {
  link: KellyLink | null
  accountValue: string | null
  navigate: (type: KellyLinkType | "404" | null, id: string | null, options?: { replace?: boolean }) => boolean
}

export const KellyLinkContext = createContext<KellyLinkContextValue>({ link: null, accountValue: null, navigate: () => false })

export function useKellyLink<Type extends KellyLinkType>(type: Type): KellyLinkLoad<KellyLinkValue<Type>> {
  const { link, accountValue, navigate } = useContext(KellyLinkContext)
  const accountAlias = useAccountAlias()
  const areEnabledFeaturesResolved = useAreEnabledFeaturesResolved()
  const row = KELLY_LINK_TYPES[type]
  const id = link?.type === type ? link.id : null
  const isReady = id != null && areEnabledFeaturesResolved && accountValue?.toLowerCase() === accountAlias.toLowerCase()
  const load = row.useLoad(isReady ? id : null) as KellyLinkLoad<KellyLinkValue<Type>>
  const result: KellyLinkLoad<KellyLinkValue<Type>> = isReady ? load : { status: "pending" }
  const { status } = result

  useEffect(() => {
    if (id == null) return
    if (status === "not-found") navigate("404", null, { replace: true })
  }, [status, id, navigate])

  return result
}

export function useKellyLinkNavigate(): KellyLinkContextValue["navigate"] {
  return useContext(KellyLinkContext).navigate
}
